import React, { useCallback, useContext } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import * as styles from './offices.styles';
import { deleteOfficeById } from './services/offices.service';
import OfficeInfoOverview from './office-info-overview.component';
import { NavigateToOverview } from './office.component';
import OFFICE_INFORMATION_KEY from './consts/consts';
import BackButton from '../../core/components/back-button/back-button.component';
import PrimaryButton from '../../core/components/buttons/primary-button.component';
import Column from '../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../core/components/flex/flex.component';
import notify from '../../core/components/notify/notify.component';
import Row from '../../core/components/row/row.component';
import { IsMobileContext } from '../../core/context/is-mobile';
import RouteConstants from '../../core/routes.constants';
import { useKeycloak } from '../authentication/keycloak/KeycloakProvider';

const OfficeEdit = (): React.JSX.Element => {
  const navigate = useNavigate();
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  const { id: officeId } = useParams<{ id: string }>();
  const keycloak = useKeycloak();

  const navigateToOverview = NavigateToOverview(navigate, [OFFICE_INFORMATION_KEY]);

  // Exactly one message either way: the service reports nothing itself, so a failure is not toasted twice.
  const deleteOffice = useCallback(async (acknowledged: boolean): Promise<void> => {
    if (!officeId) {
      notify(t('office-delete-error-message'), false);
      return;
    }

    const outcome = await deleteOfficeById(officeId, keycloak, acknowledged);

    if (outcome.deleted) {
      notify(t('office-delete-success-message'), true);
      navigateToOverview();
    } else {
      notify(t(outcome.errorKey ?? 'office-delete-error-message'), false);
    }
  }, [officeId, navigateToOverview, keycloak, t]);

  // The only entry point to the floorplan editor: it edits this office's plan, so it hangs
  // off this page rather than adding another item to the sidebar.
  const openFloorPlanEditor = useCallback((): void => {
    navigate(`/${RouteConstants.PATH_OFFICES}/${officeId}${RouteConstants.PATH_FLOORPLAN_EDITOR}`);
  }, [navigate, officeId]);

  return (
    <div className={style(styles.bottomMargin)}>
      {isMobile ? (
        <Column crossAxisAlignment={CrossAxisAlignment.Stretch} className={style(styles.cornerButton(isMobile))} itemSpacing={12}>
          <BackButton title={t('my-offices')} navigate={navigateToOverview} />
          <Row itemSpacing={12}>
            <PrimaryButton onClick={openFloorPlanEditor}>{t('floorplan-editor-open')}</PrimaryButton>
            <PrimaryButton onClick={navigateToOverview}>{t('cancel')}</PrimaryButton>
          </Row>
        </Column>
      ) : (
        <Row className={style(styles.cornerButton(isMobile))} mainAxisAlignment={MainAxisAlignment.SpaceBetween}>
          <BackButton title={t('my-offices')} navigate={navigateToOverview} />
          <Row itemSpacing={12} crossAxisAlignment={CrossAxisAlignment.Center}>
            <PrimaryButton onClick={openFloorPlanEditor}>{t('floorplan-editor-open')}</PrimaryButton>
            <PrimaryButton onClick={navigateToOverview}>{t('cancel')}</PrimaryButton>
          </Row>
        </Row>
      )}
      <OfficeInfoOverview officeId={officeId} onOfficeDelete={deleteOffice} />
    </div>
  );
};

export default OfficeEdit;
